import { Injectable } from '@angular/core';
import { map } from 'rxjs';
import { ApiService } from '../../core/services/api.service';
import { ApiEnvelope } from '../models/content.models';
import type {
  CreateFeatureRequestPayload,
  FeatureRequestActivity,
  FeatureRequestAttachment,
  FeatureRequestComment,
  FeatureRequestDetail,
  FeatureRequestListResponse,
  FeatureRequestStats,
  ListFeatureRequestsParams,
} from '../models/feature-request.models';

function toQuery(params: ListFeatureRequestsParams): string {
  const q = new URLSearchParams();
  if (params.page) q.set('page', String(params.page));
  if (params.pageSize) q.set('pageSize', String(params.pageSize));
  if (params.search?.trim()) q.set('search', params.search.trim());
  if (params.status) q.set('status', params.status);
  if (params.excludeStatuses) q.set('excludeStatuses', params.excludeStatuses);
  if (params.type) q.set('type', params.type);
  if (params.priority) q.set('priority', params.priority);
  if (params.assignedTo) q.set('assignedTo', params.assignedTo);
  if (params.createdBy) q.set('createdBy', params.createdBy);
  if (params.createdFrom) q.set('createdFrom', params.createdFrom);
  if (params.createdTo) q.set('createdTo', params.createdTo);
  if (params.sortBy) q.set('sortBy', params.sortBy);
  if (params.sortDir) q.set('sortDir', params.sortDir);
  const s = q.toString();
  return s ? `?${s}` : '';
}

@Injectable({ providedIn: 'root' })
export class FeatureRequestsService {
  constructor(private readonly api: ApiService) {}

  getStats() {
    return this.api
      .get<ApiEnvelope<FeatureRequestStats>>('platform/feature-requests/stats')
      .pipe(map((r) => r.data));
  }

  list(params: ListFeatureRequestsParams) {
    return this.api
      .get<ApiEnvelope<FeatureRequestListResponse>>(`platform/feature-requests${toQuery(params)}`)
      .pipe(map((r) => r.data));
  }

  getById(id: string) {
    return this.api
      .get<ApiEnvelope<FeatureRequestDetail>>(`platform/feature-requests/${id}`)
      .pipe(map((r) => r.data));
  }

  create(payload: CreateFeatureRequestPayload) {
    return this.api
      .post<ApiEnvelope<FeatureRequestDetail>>('platform/feature-requests', payload)
      .pipe(map((r) => r.data));
  }

  uploadAttachments(id: string, files: File[]) {
    const form = new FormData();
    files.forEach((file) => form.append('files', file));
    return this.api
      .post<ApiEnvelope<FeatureRequestAttachment[]>>(
        `platform/feature-requests/${id}/attachments`,
        form,
      )
      .pipe(map((r) => r.data));
  }

  updateStatus(id: string, status: string) {
    return this.api
      .patch<ApiEnvelope<FeatureRequestDetail>>(`platform/feature-requests/${id}/status`, { status })
      .pipe(map((r) => r.data));
  }

  assign(id: string, assignedTo: string | null) {
    return this.api
      .patch<ApiEnvelope<FeatureRequestDetail>>(`platform/feature-requests/${id}/assign`, { assignedTo })
      .pipe(map((r) => r.data));
  }

  remove(id: string) {
    return this.api
      .delete<ApiEnvelope<{ deleted: boolean }>>(`platform/feature-requests/${id}`)
      .pipe(map((r) => r.data));
  }

  listComments(id: string) {
    return this.api
      .get<ApiEnvelope<FeatureRequestComment[]>>(`platform/feature-requests/${id}/comments`)
      .pipe(map((r) => r.data));
  }

  addComment(id: string, comment: string) {
    return this.api
      .post<ApiEnvelope<FeatureRequestComment>>(`platform/feature-requests/${id}/comments`, { comment })
      .pipe(map((r) => r.data));
  }

  updateComment(requestId: string, commentId: string, comment: string) {
    return this.api
      .patch<ApiEnvelope<FeatureRequestComment>>(
        `platform/feature-requests/${requestId}/comments/${commentId}`,
        { comment },
      )
      .pipe(map((r) => r.data));
  }

  deleteComment(requestId: string, commentId: string) {
    return this.api
      .delete<ApiEnvelope<{ deleted: boolean }>>(
        `platform/feature-requests/${requestId}/comments/${commentId}`,
      )
      .pipe(map((r) => r.data));
  }

  listActivity(id: string) {
    return this.api
      .get<ApiEnvelope<FeatureRequestActivity[]>>(`platform/feature-requests/${id}/activity`)
      .pipe(map((r) => r.data));
  }

  listAttachments(id: string) {
    return this.api
      .get<ApiEnvelope<FeatureRequestAttachment[]>>(`platform/feature-requests/${id}/attachments`)
      .pipe(map((r) => r.data));
  }
}
